import type { ButtonProps } from '@mui/material';
import type { GridColDef, GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import type {
  PatientReadinessDetailsAppointment,
  PatientReadinessDetailsBucket,
  PatientReadinessDetailsResponse,
  PatientReadinessRunBucket,
} from '#services/api/patient-readiness.ts';
import type { GetMeResponsePayload } from '#services/api/user.ts';

import * as React from 'react';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { Dialog, FontAwesomeScalableIcon } from '@mend/mui';
import { Alert, Box, Button, Card, Container, Skeleton, Stack, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { usePostHog } from '@posthog/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { z } from 'zod';

import { useLocalState } from '#hooks/local-state.ts';
import { patientReadinessDetails } from '#lib/react-query/queries.ts';
import { createPatientReadinessRun } from '#services/api/patient-readiness.ts';
import { FeedbackModal } from './FeedbackModal.tsx';
import { ReadinessWaitlist } from './ReadinessWaitlist.tsx';

const highRiskLimit = 20;

const buckets = [
  {
    key: 'unconfirmed',
    label: (count: number) => `Confirm ${count} upcoming ${pluralize(count, 'appointment')}`,
    description: (amount: number) => `Reduce No Shows · Protect $${amount} in revenue`,
    emptyLabel: 'All upcoming appointments are confirmed',
    emptyDescription: 'No confirmation work is needed right now',
    ctaLabel: `Show me the top ${highRiskLimit}`,
  },
  {
    key: 'unreachable',
    label: (count: number) =>
      `${count} ${pluralize(count, 'appointment')} ${count === 1 ? 'has' : 'have'} unreachable patients, reconnect with them`,
    description: () =>
      `Download a shareable report of upcoming appointments with unreachable patients`,
    emptyLabel: 'All upcoming patients are reachable',
    emptyDescription: 'No report is needed right now',
    ctaLabel: 'View report',
  },
] as const;

const rate = 100;

export function Readiness(props: { me: GetMeResponsePayload }) {
  const query = useQuery(patientReadinessDetails);
  const [selectedDetailsBucket, setSelectedDetailsBucket] =
    React.useState<PatientReadinessRunBucket | null>(null);
  const [isFeedbackOpen, setIsFeedbackOpen] = React.useState(false);

  return (
    <>
      <Container maxWidth="md">
        <Box
          display="grid"
          columnGap={2}
          rowGap={1}
          gridTemplateColumns="min-content 1fr"
          alignItems="center"
        >
          <Box
            padding={0.75}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="99px"
            bgcolor="quaternary.main"
            color="quaternary.contrastText"
          >
            <FontAwesomeScalableIcon icon={faSparkles} />
          </Box>

          <Stack alignItems="flex-end" spacing={0.5}>
            <Typography component="span" variant="subtitle2">
              Mia
            </Typography>
          </Stack>

          <Box gridColumn="2 / span 1">
            {query.isLoading ? (
              <Stack direction="column" spacing={2} width="100%">
                <Skeleton variant="rounded" height="25px" />
                <Skeleton variant="rounded" height="22px" />
                <Skeleton variant="rounded" height="80px" />
                <Skeleton variant="rounded" height="80px" />
              </Stack>
            ) : query.isError || !query.data ? (
              <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
                Readiness summary could not load. Please try again later.
              </Alert>
            ) : (
              (() => {
                const totalAtRiskCount =
                  query.data.unconfirmed.data.length + query.data.unreachable.data.length;
                const hasAtRiskAppointments = totalAtRiskCount > 0;

                return (
                  <Stack direction="column" spacing={2}>
                    <Typography component="p" variant="subtitle1">
                      {hasAtRiskAppointments
                        ? `Assuming a $${rate} reimbursable rate, you have $${totalAtRiskCount * rate} at risk in the next 2 days`
                        : 'No appointment revenue is at risk in the next 2 days'}
                    </Typography>

                    <Typography component="p" variant="body1" color="text.secondary">
                      {hasAtRiskAppointments
                        ? 'Based on your current attendance rate, here are actions I recommend to reduce that risk:'
                        : 'There are no readiness actions to take right now.'}
                    </Typography>

                    <Stack direction="column" spacing={1.5}>
                      {buckets.map((category) => {
                        const bucketDetails = query.data[category.key];
                        const count = bucketDetails.data.length;
                        const hasAppointments = count > 0;
                        const hasRun = bucketDetails.source === 'snapshot';

                        return (
                          <Card
                            key={category.key}
                            data-testid={`bucket-${category.key}`}
                            sx={{
                              padding: 2,
                              borderWidth: 1,
                              borderStyle: 'solid',
                              borderColor:
                                hasRun || !hasAppointments ? 'success.main' : 'transparent',
                            }}
                          >
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              alignItems={{ md: 'center' }}
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Stack direction="column" spacing={0.25} flexGrow={1}>
                                <Typography component="span" variant="body1">
                                  {hasAppointments ? category.label(count) : category.emptyLabel}
                                  {hasAppointments && bucketDetails.source === 'snapshot' ? (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="success.main"
                                      sx={{ marginLeft: 0.5 }}
                                    >
                                      {`· Handled at ${format(new Date(bucketDetails.run.createdAt), 'h:mmaaa')}`}
                                    </Typography>
                                  ) : null}
                                </Typography>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {hasAppointments
                                    ? category.description(count * rate)
                                    : category.emptyDescription}
                                </Typography>
                              </Stack>

                              <Stack
                                flexShrink={0}
                                direction={{ xs: 'column', sm: 'row' }}
                                alignSelf={{ xs: 'stretch', sm: 'flex-end', md: 'center' }}
                              >
                                {hasAppointments ? (
                                  <Button
                                    color={!hasRun ? 'quaternary' : 'neutral'}
                                    variant={!hasRun ? 'contained' : 'text'}
                                    startIcon={
                                      !hasRun ? (
                                        <FontAwesomeScalableIcon icon={faSparkles} />
                                      ) : undefined
                                    }
                                    sx={{ flexShrink: 0 }}
                                    onClick={() => {
                                      setSelectedDetailsBucket(category.key);
                                    }}
                                  >
                                    {!hasRun ? category.ctaLabel : 'View details'}
                                  </Button>
                                ) : null}
                              </Stack>
                            </Stack>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Stack>
                );
              })()
            )}
          </Box>
        </Box>

        <ReadinessWaitlist />

        <Stack direction="row" justifyContent="center" marginTop={3}>
          <Button variant="text" size="small" onClick={() => setIsFeedbackOpen(true)}>
            Share your feedback
          </Button>
        </Stack>
      </Container>

      {query.data && selectedDetailsBucket ? (
        <ReadinessDetailsDialog
          me={props.me}
          bucket={selectedDetailsBucket}
          bucketDetails={query.data[selectedDetailsBucket]}
          asOf={query.dataUpdatedAt ? new Date(query.dataUpdatedAt).toISOString() : undefined}
          onClose={() => setSelectedDetailsBucket(null)}
        />
      ) : null}

      {isFeedbackOpen ? <FeedbackModal onClose={() => setIsFeedbackOpen(false)} /> : null}
    </>
  );
}

interface ReadinessCtaButtonBaseProps {
  label: string;
  asOf?: string;
  size?: ButtonProps['size'];
  onHandled?: () => void;
}

interface UnconfirmedReadinessCtaButtonProps extends ReadinessCtaButtonBaseProps {
  bucket: 'unconfirmed';
  getSelectedAppointmentIds: () => number[];
}

interface UnreachableReadinessCtaButtonProps extends ReadinessCtaButtonBaseProps {
  bucket: 'unreachable';
  appointments: PatientReadinessDetailsAppointment<'unreachable'>[];
}

type ReadinessCtaButtonProps =
  | UnconfirmedReadinessCtaButtonProps
  | UnreachableReadinessCtaButtonProps;

function ReadinessCtaButton(props: ReadinessCtaButtonProps) {
  const queryClient = useQueryClient();
  const createRunMutation = useMutation({
    mutationFn: createPatientReadinessRun,
    onSuccess: (response, variables) => {
      const patientReadinessDetailsUpdatedAt = queryClient.getQueryState(
        patientReadinessDetails.queryKey
      )?.dataUpdatedAt;

      queryClient.setQueryData<PatientReadinessDetailsResponse>(
        patientReadinessDetails.queryKey,
        (currentData) => {
          if (!currentData) {
            return currentData;
          }

          return {
            ...currentData,
            [variables.bucket]: {
              ...currentData[variables.bucket],
              source: 'snapshot',
              run: response.data.run,
              data:
                variables.bucket === 'unconfirmed'
                  ? currentData.unconfirmed.data.filter((appointment) =>
                      variables.appointmentIds.includes(appointment.id)
                    )
                  : currentData.unreachable.data,
            },
          };
        },
        patientReadinessDetailsUpdatedAt
          ? { updatedAt: patientReadinessDetailsUpdatedAt }
          : undefined
      );
      props.onHandled?.();
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        enqueueSnackbar('Mia is already working on this readiness bucket.', { variant: 'error' });
        return;
      }

      enqueueSnackbar('Mia could not start this work. Please try again later.', {
        variant: 'error',
      });
    },
  });

  function performAction() {
    if (props.bucket === 'unreachable') {
      downloadUnreachablePatientsCsv(props.appointments);
      createRunMutation.mutate({ bucket: 'unreachable', asOf: props.asOf });
      return;
    }

    createRunMutation.mutate({
      bucket: 'unconfirmed',
      asOf: props.asOf,
      appointmentIds: props.getSelectedAppointmentIds(),
    });
  }

  return (
    <Button
      color="quaternary"
      startIcon={<FontAwesomeScalableIcon icon={faSparkles} />}
      sx={{ flexShrink: 0 }}
      loading={createRunMutation.isPending}
      loadingPosition="start"
      disabled={createRunMutation.isPending}
      size={props.size ?? 'medium'}
      onClick={performAction}
    >
      {props.label}
    </Button>
  );
}

function downloadUnreachablePatientsCsv(
  appointments: PatientReadinessDetailsAppointment<'unreachable'>[]
) {
  const rows = appointments.map((appointment) => [
    formatPersonName(appointment.patient),
    format(new Date(appointment.startDate), 'LLL d, h:mmaaa'),
    formatPersonName(appointment.provider),
    appointment.appointmentType.name,
    `$${rate}`,
  ]);
  const csv = [['Patient', 'Appointment', 'Provider', 'Type', 'Revenue'], ...rows]
    .map((row) => row.map((value) => `"${value.replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = 'mia-unreachable.csv';
  anchor.click();

  // Defer cleanup so the browser can consume the object URL after the click
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function pluralize(count: number, singular: string) {
  return count === 1 ? singular : `${singular}s`;
}

const columns: GridColDef<
  | PatientReadinessDetailsAppointment<'unconfirmed'>
  | PatientReadinessDetailsAppointment<'unreachable'>
>[] = [
  {
    field: 'patientName',
    headerName: 'Patient',
    valueGetter: (_value, row) => formatPersonName(row.patient),
  },
  {
    field: 'appointmentDate',
    headerName: 'Appointment',
    valueGetter: (_value, row) => new Date(row.startDate),
    valueFormatter: (value: Date) => format(value, 'LLL d, h:mmaaa'),
  },
  {
    field: 'confirmed',
    headerName: 'Confirmed',
    minWidth: 180,
    valueGetter: (_value, row) => (row.confirmed ? new Date(row.confirmed) : null),
    valueFormatter: (value: Date | null) => (value ? format(value, 'LLL d, h:mmaaa') : 'Pending'),
    renderCell: (params) =>
      params.value ? (
        <Stack direction="row" alignItems="center" spacing={0.75} component="span">
          <FontAwesomeScalableIcon icon={faCircleCheck} sx={{ color: 'success.main' }} />
          <Typography variant="inherit" color="success.main">
            {params.formattedValue}
          </Typography>
        </Stack>
      ) : (
        <Typography variant="inherit" color="text.secondary">
          Pending
        </Typography>
      ),
  },
  {
    field: 'providerName',
    headerName: 'Provider',
    valueGetter: (_value, row) => formatPersonName(row.provider),
  },
  {
    field: 'appointmentType',
    headerName: 'Type',
    valueGetter: (_value, row) => row.appointmentType.name,
  },
  {
    field: 'attendanceRate',
    headerName: 'Risk Reason',
    sortable: false,
    valueGetter: (_value, row) => {
      const history = 'history' in row.patient ? row.patient.history : null;
      if (!history || history.totalAppointmentsInWindow === 0) return 0;
      return (history.noShowCount + history.lateCancelCount) / history.totalAppointmentsInWindow;
    },
    valueFormatter: (_value, row) => {
      if (!('history' in row.patient)) return '';
      return [
        row.patient.history.noShowCount > 0 ? 'Historical No Show' : null,
        row.patient.history.lateCancelCount > 0 ? 'Historical Late Cancel' : null,
        row.patient.history.isNewPatient ? 'New Patient' : null,
        'Unconfirmed',
      ]
        .filter(Boolean)
        .join(', ');
    },
  },
];

function formatPersonName(person: { firstName?: string; lastName?: string }) {
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ');
  return name || '--';
}

function Placeholder(props: { children: string; color?: string }) {
  return (
    <Typography component="span" variant="inherit" color="inherit">
      {'{'}
      <Typography component="span" variant="inherit" color={props.color ?? 'text.primary'}>
        {props.children}
      </Typography>
      {'}'}
    </Typography>
  );
}

const hasJoinedWaitlistSchema = z.boolean();

const miaProPerks = [
  {
    title: 'Unlimited patient outreach',
    description: `Confirm care with every appointment, not just top ${highRiskLimit}`,
  },
  {
    title: 'Automatic confirmations',
    description: 'Mia runs daily without you needing to review and approve',
  },
  {
    title: 'Priority risk scoring',
    description: "Advanced model trained on your organization's no-show history",
  },
  {
    title: 'Custom branding',
    description:
      'Customize your Care Coordinator to match the tone and uniqueness of your organization',
  },
] as const;

/**
 * Early Access Feature key from PostHog (Early access features → "Readiness agent actions").
 * Joining the waitlist enrolls the user in this feature.
 */
const readinessAgentActionsFeatureKey = 'readiness-agent-actions';

function ReadinessDetailsDialog<TBucket extends PatientReadinessRunBucket>(props: {
  me: GetMeResponsePayload;
  bucket: TBucket;
  bucketDetails: PatientReadinessDetailsBucket<TBucket>;
  asOf?: string;
  onClose: () => void;
}) {
  const titleId = React.useId();
  const waitlistTitleId = React.useId();
  const waitlistDescriptionId = React.useId();

  const [initialRowSelectionModel] = React.useState<GridRowSelectionModel>(() => ({
    type: 'include',
    ids: new Set(props.bucketDetails.data.slice(0, highRiskLimit).map((row) => row.id)),
  }));
  const [rowSelectionModel, setRowSelectionModel] = React.useState(initialRowSelectionModel);

  const [isWaitlistDialogOpen, setIsWaitlistDialogOpen] = React.useState(false);
  const [hasJoinedWaitlist, setHasJoinedWaitlist] = useLocalState({
    key: 'mia-readiness-actions-waitlist',
    defaultValue: false,
    schema: hasJoinedWaitlistSchema,
  });

  const posthog = usePostHog();

  function handleRowSelectionModelChange(nextSelectionModel: GridRowSelectionModel) {
    if (hasJoinedWaitlist) {
      setRowSelectionModel(nextSelectionModel);
      return;
    }

    const normalizedSelectedGridRowIds =
      nextSelectionModel.type === 'include'
        ? nextSelectionModel.ids
        : new Set(
            props.bucketDetails.data
              .filter((row) => !nextSelectionModel.ids.has(row.id))
              .map((row) => row.id)
          );

    const selectionsByGroup = Array.from(normalizedSelectedGridRowIds).reduce(
      (groups, rowId) => {
        if (initialRowSelectionModel.ids.has(rowId)) {
          groups.initial.add(rowId);
        } else {
          groups.extra.add(rowId);
        }
        return groups;
      },
      { initial: new Set<GridRowId>(), extra: new Set<GridRowId>() }
    );

    if (selectionsByGroup.extra.size > 0) {
      setIsWaitlistDialogOpen(true);
      return;
    }

    setRowSelectionModel({ type: 'include', ids: selectionsByGroup.initial });
  }

  function closeWaitlistDialog() {
    setIsWaitlistDialogOpen(false);
  }

  function joinWaitlist() {
    // Optimistic: flip immediately and fire the PostHog calls in the background. Enrollment is
    // best-effort (client-side, ad-blockable) and we don't gate the UI on it.
    setHasJoinedWaitlist(true);
    enqueueSnackbar(
      `You have joined the waitlist! You can now select more than ${highRiskLimit} appointments.`,
      { variant: 'success' }
    );

    // Identity is sent only here, at the explicit opt-in — everywhere else stays anonymous.
    // Identify before enrolling so `person_profiles: 'identified_only'` keeps the enrollment.
    posthog.identify(String(props.me.user.id), {
      email: props.me.user.email,
      role: props.me.user.role,
      org_id: props.me.org.id,
      org_name: props.me.org.name,
    });

    posthog.updateEarlyAccessFeatureEnrollment(readinessAgentActionsFeatureKey, true);

    closeWaitlistDialog();
  }

  const isRowSelectionEnabled =
    props.bucket === 'unconfirmed' && props.bucketDetails.source === 'current';

  return (
    <>
      <Dialog.Root
        open
        onClose={props.onClose}
        fullWidth
        fullHeight
        maxWidth="md"
        aria-labelledby={titleId}
      >
        <Dialog.Header>
          <Dialog.Title id={titleId}>
            {props.bucket === 'unconfirmed'
              ? `Top ${highRiskLimit} High-Risk Appointments`
              : 'Unreachable Patients'}
          </Dialog.Title>
        </Dialog.Header>

        <Dialog.Content>
          <Stack direction="column" spacing={2} height="100%" minHeight={0}>
            {props.bucket === 'unconfirmed' ? (
              <Card sx={{ padding: 2, flexShrink: 0 }}>
                <Stack direction="column" spacing={1}>
                  <Typography component="p" variant="body1">
                    Message to be sent:
                  </Typography>
                  <Typography
                    component="p"
                    variant="body2"
                    color="text.secondary"
                    fontStyle="italic"
                  >
                    Hi, <Placeholder>name</Placeholder>
                    <br />
                    I&apos;m Mia, a care coordinator with <Placeholder>org name</Placeholder>. I
                    noticed you haven&apos;t confirmed your appointment with{' '}
                    <Placeholder>provider</Placeholder> on <Placeholder>day</Placeholder> at{' '}
                    <Placeholder>time</Placeholder>. Will you be there? Please click this link to
                    confirm: <Placeholder color="info.main">link</Placeholder>
                  </Typography>
                </Stack>
              </Card>
            ) : null}

            <DataGridPro
              rows={
                props.bucketDetails.data as (
                  | PatientReadinessDetailsAppointment<'unconfirmed'>
                  | PatientReadinessDetailsAppointment<'unreachable'>
                )[]
              }
              columns={columns}
              hideFooter
              showToolbar
              checkboxSelection={isRowSelectionEnabled}
              disableRowSelectionOnClick
              rowSelectionModel={isRowSelectionEnabled ? rowSelectionModel : undefined}
              onRowSelectionModelChange={
                isRowSelectionEnabled ? handleRowSelectionModelChange : undefined
              }
              disableColumnSelector
              disableColumnFilter
              autosizeOnMount
              autosizeOptions={{ expand: true }}
              sx={{ flex: 1, minHeight: 0 }}
              initialState={{
                sorting: {
                  sortModel:
                    props.bucket === 'unconfirmed'
                      ? [
                          { field: 'attendanceRate', sort: 'desc' },
                          { field: 'appointmentDate', sort: 'asc' },
                        ]
                      : [{ field: 'appointmentDate', sort: 'asc' }],
                },
                columns: {
                  columnVisibilityModel: {
                    attendanceRate: props.bucket === 'unconfirmed',
                    confirmed:
                      props.bucket === 'unconfirmed' && props.bucketDetails.source === 'snapshot',
                  },
                },
              }}
              slotProps={{
                toolbar: {
                  csvOptions: { fileName: `mia-${props.bucket}` },
                  printOptions: { disableToolbarButton: true },
                },
              }}
            />
          </Stack>
        </Dialog.Content>

        <Dialog.Footer>
          <Dialog.DismissButton />

          {props.bucketDetails.source === 'current' ? (
            props.bucket === 'unreachable' ? (
              <ReadinessCtaButton
                bucket="unreachable"
                label="Download report"
                asOf={props.asOf}
                size="large"
                onHandled={props.onClose}
                appointments={
                  props.bucketDetails.data as PatientReadinessDetailsAppointment<'unreachable'>[]
                }
              />
            ) : (
              <ReadinessCtaButton
                bucket="unconfirmed"
                label="Confirm for me"
                getSelectedAppointmentIds={() =>
                  props.bucketDetails.data
                    .filter((appointment) =>
                      rowSelectionModel.type === 'include'
                        ? rowSelectionModel.ids.has(appointment.id)
                        : !rowSelectionModel.ids.has(appointment.id)
                    )
                    .map((appointment) => appointment.id)
                }
                asOf={props.asOf}
                size="large"
                onHandled={props.onClose}
              />
            )
          ) : null}
        </Dialog.Footer>
      </Dialog.Root>

      {isWaitlistDialogOpen && (
        <Dialog.Root
          open
          onClose={closeWaitlistDialog}
          maxWidth="sm"
          fullWidth
          aria-labelledby={waitlistTitleId}
          aria-describedby={waitlistDescriptionId}
          slotProps={{
            paper: { sx: { borderWidth: 1, borderStyle: 'solid', borderColor: 'quaternary.main' } },
          }}
        >
          <Dialog.Header>
            <Dialog.Title id={waitlistTitleId}>Upgrade to Mia Pro</Dialog.Title>
            {/* TODO: Per cycle wording */}
            <Dialog.Description id={waitlistDescriptionId}>
              Your current plan includes outreach for up to {highRiskLimit} high-risk patients per
              cycle. Upgrade to automatically reduce no shows for all patients with Mia.
            </Dialog.Description>
          </Dialog.Header>

          <Dialog.Content>
            <Card component={Stack} direction="column" spacing={1.5} padding={2} marginBottom={2}>
              {miaProPerks.map((perk) => (
                <Box
                  key={perk.title}
                  display="grid"
                  gridTemplateColumns="min-content 1fr"
                  columnGap={1.5}
                  alignItems="center"
                >
                  <FontAwesomeScalableIcon icon={faCircleCheck} color="success" />

                  <Typography variant="body1">{perk.title}</Typography>
                  <Typography variant="body2" color="text.secondary" gridColumn="2 / span 1">
                    {perk.description}
                  </Typography>
                </Box>
              ))}
            </Card>

            <Card sx={{ padding: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <Typography component="span" color="quaternary.main" variant="inherit">
                  Average impact at full coverage:
                </Typography>{' '}
                {/* TODO: Percentage */}
                Organizations using unlimited outreach see 18% fewer no-shows month over month.
              </Typography>
            </Card>
          </Dialog.Content>

          <Dialog.Footer>
            <Button color="neutral" variant="outlined" size="large" onClick={closeWaitlistDialog}>
              Stay on free
            </Button>

            <Button
              variant="contained"
              color="quaternary"
              size="large"
              startIcon={<FontAwesomeScalableIcon icon={faSparkles} />}
              onClick={joinWaitlist}
            >
              Join the waitlist
            </Button>
          </Dialog.Footer>
        </Dialog.Root>
      )}
    </>
  );
}
